import { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { TOURS } from '@/lib/guidedTours';

const GuidedTourContext = createContext(null);

/** Holds the active tour + handlers. Navigates to the tour's route before
 * firing so steps targeting that page's DOM are actually mounted. */
export const GuidedTourProvider = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTourId, setActiveTourId] = useState(null);
  const [pendingTourId, setPendingTourId] = useState(null);

  const startTour = useCallback((tourId) => {
    const tour = TOURS[tourId];
    if (!tour) {
      // eslint-disable-next-line no-console
      console.warn(`Unknown tour: ${tourId}`);
      return;
    }
    if (location.pathname !== tour.route) {
      // Navigate first; the effect below promotes pending→active once the route matches
      setPendingTourId(tourId);
      navigate(tour.route);
    } else {
      setActiveTourId(tourId);
    }
  }, [navigate, location.pathname]);

  const stopTour = useCallback(() => {
    setActiveTourId(null);
    setPendingTourId(null);
  }, []);

  // Promote pending → active once we've arrived on the tour's route AND its
  // first selector exists (avoids racing the initial render on slow pages).
  useEffect(() => {
    if (!pendingTourId) return;
    const tour = TOURS[pendingTourId];
    if (!tour || location.pathname !== tour.route) return;

    const firstSelector = tour.steps?.[0]?.target;
    let attempts = 0;
    const id = setInterval(() => {
      attempts += 1;
      const found = firstSelector ? document.querySelector(firstSelector) : true;
      if (found || attempts >= 40) {
        setActiveTourId(pendingTourId);
        setPendingTourId(null);
        clearInterval(id);
      }
    }, 150);
    return () => clearInterval(id);
  }, [pendingTourId, location.pathname]);

  const value = { activeTourId, startTour, stopTour };
  return <GuidedTourContext.Provider value={value}>{children}</GuidedTourContext.Provider>;
};

export const useGuidedTour = () => {
  const ctx = useContext(GuidedTourContext);
  if (!ctx) throw new Error('useGuidedTour must be used inside <GuidedTourProvider>');
  return ctx;
};
